"use client";

import Image from "next/image";
import { Mic, Video, Calendar, CheckCircle, ExternalLink, Play } from "lucide-react";
import { interviews, certifications } from "@/lib/data"; 

// Helper to get YouTube ID from various URL formats
const getYouTubeId = (url: string) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

export default function CompanyPage() {
  const leaders = [
    { name: "Joel Garcia", role: "CEO & Founder", bio: "Former CTO turned agency founder. Cabot Cosmos Star.", image: "/team/joel.jpg" },
    { name: "Mark Paci", role: "COO", bio: "The New Guy. Does lots of stuff.", image: "/team/ray.jpg" },
    { name: "Luke Lightning", role: "Head of Sales & Biz Dev", bio: "Certified Drinking Buddy.", image: "/team/tawheed.jpg" },
    { name: "Mike Alvarez", role: "Director of Engineering", bio: "Stud 1", image: "/team/joel.jpg" },
    { name: "Hans Osipina", role: "Director of Engineering", bio: "Stud 2", image: "/team/ray.jpg" },
    { name: "Andres Jaramillo", role: "Director of Engineering", bio: "Stud 3", image: "/team/tawheed.jpg" },
  ];

  // Filter lists (Note: "Link" is treated as a video card but rendered differently)
  const videos = interviews.filter(i => i.type === "Video" || i.type === "Link");
  const audios = interviews.filter(i => i.type === "Audio");

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* HERO */}
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6">
          Builders, Not Salespeople.
        </h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          We are a team of architects and contributors delivering high-performance cloud solutions.
        </p>
      </div>

      {/* LEADERSHIP */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Leadership Team</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {leaders.map((leader) => (
              <div key={leader.name} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div className="h-64 w-full bg-slate-200 rounded-lg mb-4 flex items-center justify-center text-slate-400">
                   <span className="text-xs font-mono">[Photo: {leader.name}]</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900">{leader.name}</h3>
                <p className="text-primary font-mono text-sm mb-2 font-medium">{leader.role}</p>
                <p className="text-slate-600 text-sm">{leader.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEDIA SECTION */}
      <section className="py-20 max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-slate-900">Interviews & Media</h2>
          <p className="text-slate-500 mt-2">Insights from our leadership on Cloud, AI, and Scaling.</p>
        </div>
        
        {/* VIDEOS ROW */}
        <div className="mb-16">
          <div className="flex items-center gap-2 mb-6 text-slate-900 font-bold text-xl border-b border-slate-200 pb-2">
             <Video className="text-red-600 h-5 w-5" /> Watch
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {videos.map((item, i) => {
              const videoId = getYouTubeId(item.url);
              
              return (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200 flex flex-col h-full">
                  <div className="aspect-video w-full bg-slate-900 relative">
                    {/* CASE 1: YOUTUBE EMBED */}
                    {item.type === "Video" && videoId ? (
                      <iframe 
                        width="100%" 
                        height="100%" 
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={item.title}
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                      ></iframe>
                    ) : (
                      /* CASE 2: EXTERNAL LINK (Riverside) */
                      <a 
                        href={item.url} 
                        target="_blank" 
                        className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800 hover:bg-slate-700 transition-colors group text-white"
                      >
                         <Play className="h-12 w-12 mb-2 group-hover:scale-110 transition-transform" />
                         <span className="font-bold text-sm">Watch on {item.source.split(' ')[0]}</span>
                      </a>
                    )}
                  </div>
                  <div className="p-4 flex flex-col grow">
                    <div className="text-xs font-mono text-slate-500 mb-2 flex items-center gap-2">
                       <Calendar className="h-3 w-3" /> {item.date}
                    </div>
                    <h3 className="font-bold text-slate-900 leading-tight mb-2 text-sm md:text-base">{item.title}</h3>
                    <p className="text-xs text-slate-500 uppercase tracking-wide mt-auto">{item.source}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AUDIO ROW */}
        <div>
          <div className="flex items-center gap-2 mb-6 text-slate-900 font-bold text-xl border-b border-slate-200 pb-2">
             <Mic className="text-purple-600 h-5 w-5" /> Listen
          </div>
          <div className="grid md:grid-cols-2 gap-8">
             {audios.map((item, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                   {/* Apple Podcasts Embed */}
                   <iframe 
                     allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write" 
                     frameBorder="0" 
                     height="175" 
                     style={{width:'100%', maxWidth:'660px', overflow:'hidden', background:'transparent'}} 
                     sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" 
                     src={item.url}>
                   </iframe>
                </div>
             ))}
          </div>
        </div>
      </section>

    </div>
  );
}